import { useState, useMemo, Fragment } from 'react';
import { useChartContext, COLOR_VISION_SINGLE_ITEM, HORIZONTAL_GROUP_LABEL_HEIGHT, BORDER_RADIUS } from '@shopify/polaris-viz-core';
import { jsx } from 'react/jsx-runtime';
import { useWatchColorVisionEvents } from '../../../hooks/ColorVisionA11y/useWatchColorVisionEvents.js';
import { useStackedGaps } from './hooks/useStackedGaps.js';
import { getXPosition } from './utilities/getXPosition.js';
import { ZeroValueLine } from '../ZeroValueLine/ZeroValueLine.js';
import { StackedBar } from './components/StackedBar/StackedBar.js';
import { getGradientDefId } from '../GradientDefs/GradientDefs.js';
import { getBarId } from '../../../utilities/getBarId.js';

function getBorderRadius({
  lastIndexes,
  seriesIndex
}) {
  const [positive, negative] = lastIndexes;

  if (positive === seriesIndex) {
    return BORDER_RADIUS.Right;
  }

  if (negative === seriesIndex) {
    return BORDER_RADIUS.Left;
  }

  return BORDER_RADIUS.None;
}

function HorizontalStackedBars({
  activeGroupIndex,
  animationDelay,
  barHeight,
  dataKeys,
  groupIndex,
  id,
  name,
  stackedValues,
  xScale
}) {
  const {
    theme
  } = useChartContext();
  const [activeBarIndex, setActiveBarIndex] = useState(-1);
  useWatchColorVisionEvents({
    type: COLOR_VISION_SINGLE_ITEM,
    onIndexChange: ({
      detail
    }) => {
      if (activeGroupIndex === -1 || activeGroupIndex === groupIndex) {
        setActiveBarIndex(detail.index);
      }
    }
  });
  const lastIndexes = useMemo(() => {
    let lastPos = -1;
    let lastNeg = -1;
    stackedValues[groupIndex].forEach(([start, end], index) => {
      if (start < 0) {
        lastNeg = index;
      }

      if (end > 0) {
        lastPos = index;
      }
    });
    return [lastPos, lastNeg];
  }, [groupIndex, stackedValues]);
  const gaps = useStackedGaps({
    stackedValues,
    groupIndex
  });
  return /*#__PURE__*/jsx("g", {
    style: {
      transform: `translate(0, ${HORIZONTAL_GROUP_LABEL_HEIGHT}px`
    },
    "aria-hidden": "true",
    children: stackedValues[groupIndex].map((item, seriesIndex) => {
      var _dataKeys$seriesIndex;

      const [start, end] = item;
      const barId = getBarId(id, groupIndex, seriesIndex);
      const width = Math.abs(xScale(end) - xScale(start));
      const borderRadius = getBorderRadius({
        lastIndexes,
        seriesIndex
      });
      const x = getXPosition({
        start,
        end,
        seriesIndex,
        gaps,
        xScale
      });
      const key = (_dataKeys$seriesIndex = dataKeys[seriesIndex]) !== null && _dataKeys$seriesIndex !== void 0 ? _dataKeys$seriesIndex : '';
      const ariaLabel = `${key} ${end}`;
      const areAllValuesZero = stackedValues[groupIndex].every(([start, end]) => start + end === 0);
      return /*#__PURE__*/jsx(Fragment, {
        children: areAllValuesZero ? /*#__PURE__*/jsx(ZeroValueLine, {
          x: x,
          y: barHeight / 2,
          direction: "horizontal"
        }) : /*#__PURE__*/jsx(StackedBar, {
          animationDelay: animationDelay,
          activeBarIndex: activeBarIndex,
          ariaLabel: ariaLabel,
          borderRadius: borderRadius,
          color: getGradientDefId(theme, seriesIndex, id),
          height: barHeight,
          seriesIndex: seriesIndex,
          setActiveBarIndex: setActiveBarIndex,
          width: width,
          x: x,
          zeroPosition: xScale(0)
        }, `${name}${barId}`)
      }, `stackedBar ${barId}`);
    })
  });
}

export { HorizontalStackedBars };
